<template>
  <MainLayout v-scroll="onScroll" bg-color="white" no-content-padding scroll-toolbar>
    <template #toolbar>
      <LensReportHeader
        :report-id="report?.id ?? $route.params.reportId"
        :title="report?.name"
        :description="report?.description"
        :loading="loading.report"
        :attribution-window="{click: report?.click_attribution_window, view: report?.view_attribution_window}"
        :lens="lens"
        :selected-date-range="selectedDateRange"
        @exportCSV="handleCSVExport"
        @showDuplicateReport="modal.showDuplicateReport = true"
        @showShareReport="modal.showShareReport = true"
        @changeWindow="handleReportChange"
        @localReportUpdate="handleUntrackedLocalReportChange"
        @refreshComplete="handleFiltersChange(appliedFilters, selectedDateRange)"
      />
    </template>
    <template #filter>
      <div ref="filterBar" class="border border-border-normal bg-white">
        <LensFilterToolbar
          :current-lens="lens"
          :loading="loading.report"
          :ad-account-options="adAccountOptions"
          :selected-account-ids="selectedAdAccountIds"
          :group-by="report?.group_by"
          :report-type="reportType"
          :total-groups="totalComparativeGroups"
          :applied-filters.sync="appliedFilters"
          :selected-date-range.sync="selectedDateRange"
          :is-group-builder-open="!!showGroupBuilder"
          enable-sticky-toggle
          :stick-to-viewport.sync="stickToViewport"
          @ad-account-selection-change="handleAdAccountSelectionChange($event)"
          @toggleComparativeBuilder="showGroupBuilder = $event ?? !showGroupBuilder"
          @update:groupBy="handleReportChange"
          @fetchGroups="fetchAdGroups($event)"
        />
      </div>
      <div v-if="reportType === 'comparative' && showGroupBuilder" class="border border-border-normal rounded-md mt-4 mx-3">
        <ComparaitiveGroupBuilder
          :current-lens="lens"
          :comparative-query.sync="comparativeQuery"
          :comparative-info="comparativeInfo"
          @showBreakdown="setComparativeBreakdown"
        />
      </div>
    </template>
    <template #content>
      <div class="bg-white">
        <div class="px-3 pt-4">
          <LensReportGraph
            :report-type="reportType"
            :comparative-query="comparativeQuery"
            :graph-type.sync="graphType"
            :data-items="graphDataItems"
            :data-summary="graphDataSummary"
            :graph-info="graphInfo"
            :result-count="tableResults?.ad_groups?.length"
            :has-mounted="!hasMounted"
            :selected-account-ids="selectedAdAccountIds"
            :selected-kpis="selectedColumns[graphType]"
            :can-add-kpis="!isSelectedColsMaxed"
            :loading-data="loading?.adGroups || loading?.report"
            @updateTimeSeries="timeseriesData = $event"
            @updateSelectedKpis="updateSelectedColumns"
            @showGroupBreakdown="handleShowGroupBreakdown"
            @showDetailsDrawer="handleShowDetailsDrawer"
          />
        </div>
        <div class="px-3 pt-3 relative">
          <!-- Table settings btn -->
          <LensTableOptions
            :loading="loading?.report"
            :table-pagination="getPagination"
            :table-columns="report?.table_columns"
            :table-config="report?.table_config"
            :search-query="searchQuery"
            :can-scroll-left="tableScrollState.canScrollLeft"
            :can-scroll-right="tableScrollState.canScrollRight"
            :handle-scroll="handleTableScroll"
            @updateSelectedKpis="updateSelectedColumns"
            @searchChanged="handleSearchChange"
            @paginationChanged="handlePaginationChange"
            @reportChanged="handleReportChange"
          />
          <Transition name="tableFade" mode="out-in">
            <!-- Table Fallback States -->
            <div v-if="loading.report || noPaginationItems || isPreparingGroups"
            class="grid grid-cols-12 grid-rows-6 gap-0 border relative border-neutral-50 mr-3 mt-3 rounded-lg">
              <!-- Loading Data-->
              <div v-if="loading.report || (noPaginationItems && loading.adGroups) || isPreparingGroups"
              class="absolute w-full h-full flex flex-col items-center justify-center gap-1 text-center">
                <div>
                  <BaseLoadingSpinnerCircle :width="24" :height="24" class="text-text-muted" />
                </div>
                <BaseText type="label" size="sm" class="text-text-muted">
                  Loading Data...
                </BaseText>
                <BaseText size="sm" class="text-text-normal w-64">
                  One moment while we load the data. This might take up to a minute.
                </BaseText>
              </div>
              <!-- No Data Found -->
              <div v-else-if="!loading.adGroups && noPaginationItems"
              class="absolute w-full h-full flex flex-col items-center justify-center gap-1 text-center">
                <div>
                  <WarningIcon class="text-icon-normal" />
                </div>
                <BaseText type="label" size="sm" class="text-text-muted">
                  No data found
                </BaseText>
                <BaseText size="sm" class="text-text-normal w-64">
                  Try changing your filters
                </BaseText>
              </div>
              <!-- Grid background -->
              <div v-for="index in 36" :key="index" class="border-neutral-50 h-20 col-span-2"
              :class="{'border-b': index <= 30, 'border-r': index % 30 !== 0}" />
            </div>
            
            <LensMetricTable
              v-else-if="getPagination.totalItems > 0"
              :key="activeReqId"
              ref="metricTable"
              class="mt-4"
              :report-type="reportType"
              :grouped-by="report?.group_by"
              :table-pagination="getPagination"

              :loading="loading.adGroups"

              :table-columns="report?.table_columns"
              :graph-info="graphInfo"
              :lens-currency="lens.currency"

              :selected-columns="selectedColumns[graphType]"
              :selected-rows.sync="selectedRows"
              :max-row-count="totalAllowedRows"
              :search-query="searchQuery"

              :table-config="report?.table_config"
              :current-sort="report?.sorted_column"
              :data="tableResults"

              :sticky-header-offset="stickyHeaderOffset"
              enable-float-header-toggle
              :float-header-enabled.sync="floatColumnHeader"

              @updateTableResults="updateTableResults"

              @update:selectedColumns="updateSelectedColumns"
              @update:selectedRows="updateSelectedRows"

              @paginationChanged="handlePaginationChange"
              @reportChanged="handleReportChange"
              @scroll-state-change="handleScrollStateChange"

              @showGroupBreakdown="handleShowGroupBreakdown"
              @showDetailsDrawer="handleShowDetailsDrawer"
            />
            <div v-else class="w-full h-full flex flex-col items-center justify-center gap-1 text-center mt-52 my-auto">
              <div>
                <WarningIcon class="text-icon-normal" />
              </div>
              <BaseText type="label" size="sm" class="text-text-muted">
                No data found
              </BaseText>
              <div class="mt-3 space-y-2">
                <BaseText size="sm" class="text-neutral-500 w-64">
                  Was this an error?
                </BaseText>
                <button class="base-btn px-3 py-1.5" @click="openIntercom">
                  <BaseText type="label" size="sm">
                    Contact Support
                  </BaseText>
                </button>
              </div>
            </div>
          </Transition>
        </div>
        <LensSettingsDrawer
          v-if="getColumnDrawerView"
          :table-columns="report?.table_columns"
          @reportChanged="handleReportChange"
          @close="closeColumnDrawer"
        />
      </div>
      <!-- MODALS -->
      <LensTagModal
        v-if="getShowEditTagModal"
        key="edit-tag-modal"
        @close="SET_SHOW_EDIT_TAG_MODAL(false)"
      />
      <EditPresetModal
        v-if="modal.showCreatePreset"
        key="new-preset"
        is-new
        @close="modal.showCreatePreset = false"
        @save="handleNewPreset"
      />
      <DuplicateReportModal
        v-if="modal.showDuplicateReport"
        :report="report"
        :report-type="reportType"
        @close="modal.showDuplicateReport = false"
      />
      <ShareReportModal
        v-if="modal.showShareReport && !loading.report"
        :report="report"
        :fetch-payload="lastUsedPayload"
        :report-type="reportType"
        :current-graph="graphType"
        :timeseries="timeseriesData"
        :current-date-range="selectedDateRange"
        :summary-stats="tableResults.summary_stats"
        :report-id="report?.id ?? $route.params?.reportId"
        :lens-id="$route.params?.lensId"
        :selected-rows="selectedRows"
        :lens-currency="lens?.currency"
        @close="modal.showShareReport = false"
      />
      <!-- Changes prompt -->
      <ReportEditSavePrompt
        ref="reportEditSavePrompt"
        :loading-info="editLoading"
        :original-report-config="originalReportConfig"
        :changed-report-config="reportConfig"
        @save="handleSaveReport"
        @cancel="handleReportCancel"
      />
      <!-- Drawers -->
      <GroupBreakdownDrawer
        v-if="showGroupBreakdown"
        :loading="loading.groupBreakdown"
        :group-data="groupBreakdownData"
        :current-lens="lens"
        :current-report="report"
        :selected-date-range="selectedDateRange"
        :report-summary-stats="tableResults.summary_stats"
        :graph-consts="{ maxRows: MAX_GRAPH_ROWS, maxCols: MAX_GRAPH_COLS }"
        :init-selected-columns="selectedColumns"
        :init-graph-type="graphType === 'line' ? 'grid' : graphType"
        :is-custom-group="reportType === 'comparative'"
        :is-parent-drawer="showCreativeDetails"
        @updateTableResults="updateTableResults"
        @showDetailsDrawer="handleShowDetailsDrawer($event)"
        @close="handleCloseBreakdown"
      />
      <AdInsightsDrawer
        v-if="showCreativeDetails"
        :brand="{ ...lens, name: lens?.company_name ?? lens?.name ?? ''}"
        :loading="loading.adDetails"
        :loading-metrics="loading.adDetailsMetrics"
        :data-summary="graphDataSummary"
        :date-range="selectedDateRange"
        :ad-group-data="adDetailsData"
        :summary-stats="tableResults.summary_stats"
        :all-ads="adDetailsData?.ads"
        :selected-columns="report?.table_columns || []"
        :is-child-drawer="showGroupBreakdown"
        @updateTableResults="updateTableResults"
        @refetch-insights="handleShowDetailsDrawer(adDetailsRow, $event)"
        @close="adDetailsData = null"
      />
    </template>
  </MainLayout>
</template>

<script>
import store from '@/store'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import _ from 'lodash'
import axios from 'axios'
import LensAPI from '@/api/lens'
import { enrichLensAdAccountsWithProviderData, reverseProviderKeyMap } from '@/utils/lens/dataSourceUtils'
import { getReportStateFromConfig, applyChangeToReportConfig, mergeUpdateToReportConfig } from '@/utils/lens/reportDataUtils'
import { createFilterQuery, parseFiltersFromQuery, parseDateRangeFromQuery } from '@/utils/lens/filters/filterQueryUtils'
import { formatGroupsAsCSV } from '../../utils/generateCSV'
import EventBus from '@/plugins/BaseNotifications/EventBus'

// Components
import MainLayout from '../../layouts/MainLayout.vue'
import LensFilterToolbar from '../../components/lens/filters/LensFilterToolbar.vue'
import LensMetricTable from '../../components/lens/table/LensMetricTable.vue'
import LensSettingsDrawer from '../../components/settings/LensSettingsDrawer.vue'
import LensTableOptions from '../../components/lens/table/LensTableOptions.vue'
import ReportEditSavePrompt from '../../components/lens/report/ReportEditSavePrompt.vue'
import LensReportGraph from '../../components/lens/graphs/LensReportGraph.vue'
import LensReportHeader from '../../components/lens/report/LensReportHeader.vue'
import GroupBreakdownDrawer from '../../components/lens/report/GroupBreakdownDrawer.vue'
import EditPresetModal from '../../components/lens/settings/EditPresetModal.vue'
import DuplicateReportModal from '../../components/lens/report/DuplicateReportModal.vue'
import ShareReportModal from '../../components/lens/report/share/ShareReportModal.vue'
import ComparaitiveGroupBuilder from '../../components/lens/report/ComparaitiveGroupBuilder.vue'
import AdInsightsDrawer from '../../components/lens/report/AdInsightsDrawer.vue'
import LensTagModal from '../../components/lens/LensTagModal.vue'

// Icons
import WarningIcon from '../../components/globals/Icons/WarningIcon.vue'

export default {
  name: 'LensReportView',
  components: {
    MainLayout,
    LensFilterToolbar,
    LensMetricTable,
    LensSettingsDrawer,
    LensReportGraph,
    LensTableOptions,
    EditPresetModal,
    ReportEditSavePrompt,
    LensReportHeader,
    GroupBreakdownDrawer,
    DuplicateReportModal,
    ComparaitiveGroupBuilder,
    ShareReportModal,
    AdInsightsDrawer,
    LensTagModal,
    // Icons
    WarningIcon
  },
  mixins: [clickaway],
  metaInfo () {
    return {
      title: this.reportPageTitle
    }
  },
  data () {
    const groupOptions = [
      { name: 'Ad Name', value: 'ad_name' },
      { name: 'Ad Copy', value: 'ad_copy' },
      { name: 'Landing Page', value: 'destination_url' },
      { name: 'Creative', value: 'creative' }
    ]
    const MAX_GRAPH_ROWS = {
      bar: 8,
      line: 8,
      grid: 999
    }
    const MAX_GRAPH_COLS = {
      bar: 6,
      line: 2,
      grid: 20
    }
    return {
      // Constants
      MAX_GRAPH_ROWS,
      MAX_GRAPH_COLS,
      groupOptions,
      
      // Lens
      lens: null,

      // =========== Report State =========== \\
      wasCustom: {},
      // originalReport: {},
      selectedAdAccountIds: [],
      reportConfig: {},
      originalReportConfig: {},
      report: {},
      reportNotFound: false,
      reportType: null,
      // Tracks which platforms have done their first report state initialization
      reportPlatformInitialized: {},
      
      // ========= Table/Graph State ======== \\
      // Table
      tableResults: {
        ad_groups: [],
        summary_stats: {}
      },
      queryCancelToken: null,
      // Graph
      graphType: 'bar',
      selectedRows: [],
      selectedColumns: {
        bar: [],
        line: [],
        grid: []
      },

      // =========== Filters State ========== \\
      comparativeQuery: [],
      appliedFilters: [],
      selectedDateRange: {
        start: null,
        end: null,
        isRelativeToToday: false
      },
      searchQuery: '',

      // =========== Drawer State =========== \\
      groupBreakdownData: null,
      adDetailsData: null,
      adDetailsRow: null,

      // =========== Share State ============ \\
      timeseriesData: null,

      // ============= UI State ============= \\
      hasMounted: false,
      showColumnDrawer: false,
      showGroupBuilder: true,
      showAdDetailsDrawer: true,
      showDropdown: {
        tableSettings: false,
        group: false
      },
      modal: {
        showCreatePreset: false,
        showDuplicateReport: false,
        showShareReport: false
      },
      loading: {
        report: true,
        adGroups: false,
        groupBreakdown: false,
        adDetails: false,
        adDetailsMetrics: false,
        cancel: false
      },
      tableScrollState: {
        canScrollLeft: false,
        canScrollRight: false
      },
      // Update type with error or success to enable ReportEditSavePrompt with ui
      editLoading: {
        type: null,
        value: false
      },

      // Whether the toolbar should stick to the viewport (persisted per-user via localStorage)
      stickToViewport: localStorage.getItem('lens_toolbar_stick_to_viewport') !== 'false',

      // Whether the table column header should float when scrolled past (also persisted per-user via local storage)
      floatColumnHeader: localStorage.getItem('lens_table_float_column_header') === 'true',

      lastUsedPayload: null,

      // Filter bar height for sticky header offset
      filterBarHeight: 0
    }
  },
  beforeRouteEnter (_to, _from, next) {
    // Make sure metrics are loaded in before entering this route
    if (store.getters['MetricsModule/getIsLoadingMetrics']) {
      const unwatch = store.watch(
        state => state.MetricsModule.isLoadingMetrics,
        isLoading => {
          if (!isLoading) {
            unwatch()
            next()
          }
        }
      )
    } else {
      next()
    }
  },
  async mounted () {
    // Initialize pagination with stored limit
    this.updatePagination({
      ...this.getPagination,
      limit: this.getStoredLimit()
    })

    // Set initial top bar styles
    this.$nextTick(() => {
      this.applyToolbarStickyStyles()
    })

    // Fetch lens & presets (if necessary)
    await Promise.all([
      this.fetchLens(),
      (!this.getPresets?.length) ? this.fetchPresets() : Promise.resolve()
    ])

    // Fetch report & initialize ad accounts
    const reportId = this.$route.params.reportId
    await this.fetchReport(reportId)
    if (this.reportNotFound) {
      this.$showAlert({ type: 'error', message: 'Report not found' })
      return
    }
    this.loading.report = false

    // Check modal init states
    if (this.getShowReportShareModal) {
      this.modal.showShareReport = true
      this.SET_SHOW_REPORT_SHARE_MODAL(false)
    }

    const preventInitFetch = this.reportType === 'comparative' ? this.comparativeQuery?.length === 0 : false
    if (!preventInitFetch) {
      await this.fetchAdGroups()
    } else {
      this.showGroupBuilder = true
    }

    // Configure event listeners (respond to ad accounts being connected/disconnected via LensAdAccountConnector)
    this._onLensConnectedAdAccounts = () => { this.fetchLens() }
    EventBus.$on('lens:connected-ad-accounts', this._onLensConnectedAdAccounts)
    this._onLensRemovedAdAccount = async () => {
      await this.fetchLens()
      this.initializeAdAccounts()
      this.report = getReportStateFromConfig(this.reportConfig)
    }
    EventBus.$on('lens:removed-ad-account', this._onLensRemovedAdAccount)

    // Add window resize listener for filter bar height
    window.addEventListener('resize', this.measureFilterBarHeight)

    this.hasMounted = true
  },
  beforeRouteLeave (_to, _from, next) {
    const savePromptComp = this.$refs.reportEditSavePrompt
    if (savePromptComp && savePromptComp.totalChanges.count > 0) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?')
      next(confirmed)
    } else {
      next()
    }
  },
  beforeDestroy () {
    if (this.queryCancelToken) this.queryCancelToken.cancel()
    EventBus.$off('lens:connected-ad-accounts', this._onLensConnectedAdAccounts)
    EventBus.$off('lens:removed-ad-account', this._onLensRemovedAdAccount)
    window.removeEventListener('resize', this.measureFilterBarHeight)
    this.updatePagination({
      currentPage: 1,
      limit: this.getStoredLimit(),
      totalItems: 0,
      totalPages: 0
    })
  },
  watch: {
    graphType (type) {
      if (this.tableResults?.ad_groups?.length > 0) {
        const rowCount = this.report?.selected_graph_rows[type]
        this.selectedRows = this.tableResults.ad_groups.slice(0, rowCount)
        this.updateRowColor({ keys: this.selectedRows, isAdding: true, graph: type, ignorePrevColors: true })
      }
      this.setReportStateFieldValue({ type: 'graph_type', value: type })
    },
    appliedFilters (filters) {
      this.handleFiltersChange(filters, this.selectedDateRange)
    },
    selectedDateRange (dateRange) {
      this.handleFiltersChange(this.appliedFilters, dateRange)
    },
    comparativeQuery (query) {
      if (this.reportType !== 'comparative' || !this.hasMounted || this.loading.cancel) return
      const noActiveConditions = query.every((q) => q.query.length === 0)
      if (query.length === 0 || noActiveConditions) {
        // If no query reset
        this.tableResults.ad_groups = []
        this.updatePagination({
          currentPage: 1,
          limit: this.getStoredLimit(),
          totalItems: 0,
          totalPages: 0
        })
        if (this.queryCancelToken) this.queryCancelToken.cancel()
        this.loading.adGroups = false
      }
      const change = { type: 'custom_groups_filters', value: query, preventFetch: query.length === 0 }
      this.handleReportChange(change)
    },
    getShowReportShareModal (newValue) {
      if (newValue) {
        this.modal.showShareReport = true
        this.SET_SHOW_REPORT_SHARE_MODAL(false)
      }
    },
    stickToViewport (value) {
      localStorage.setItem('lens_toolbar_stick_to_viewport', value ? 'true' : 'false')
      this.applyToolbarStickyStyles()
    },
    floatColumnHeader (value) {
      localStorage.setItem('lens_table_float_column_header', value ? 'true' : 'false')
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getProviders', 'getColumnDrawerView', 'getPagination', 'getShowReportShareModal', 'getShowEditTagModal']),
    ...mapGetters('MetricsModule', ['getMetricsPlatform','getMetricLookup', 'getPresets', 'getAppliedPreset']),
    stickyHeaderOffset() {
      return this.stickToViewport ? (16 + this.filterBarHeight) : 0
    },
    adAccountOptions () {
      if (!this.getProviders?.length || !this.lens?.ad_accounts?.length) return null
      return enrichLensAdAccountsWithProviderData(this.getProviders, this.lens.ad_accounts)
    },
    comparativeInfo () {
      if (!this.tableResults || !this.tableResults?.ad_groups) return

      return this.tableResults.ad_groups.reduce((acc, adGroup) => {
        const { active_ad_count: activeAds, paused_ad_count: pausedAds } = adGroup || {}
        const activeAdCount = activeAds
        const totalAds = activeAds + pausedAds
        acc[adGroup.group_by] = { adCount: totalAds, activeCount: activeAdCount }
        return acc
      }, {})
    },
    graphInfo () {
      return {
        selectedGraph: this.graphType,
        data: {
          bar: {
            selected: this.report?.selected_columns?.bar?.length || 0,
            total: this.MAX_GRAPH_COLS.bar
          },
          line: {
            selected: this.report?.selected_columns?.line?.length || 0,
            total: this.MAX_GRAPH_COLS.line
          },
          grid: {
            selected: this.report?.selected_columns?.grid?.length || 0,
            total: this.MAX_GRAPH_COLS.grid
          }
        }
      }
    },
    graphDataItems () {
      const totalItems = this.getPagination.totalItems
      return totalItems > 0 ? this.selectedRows : []
    },
    graphDataSummary () {
      return {
        ...this.tableResults.summary_stats,
        startDate: this.selectedDateRange.start,
        endDate: this.selectedDateRange.end,
        groupedBy: this.report?.group_by,
        sort: this.report?.sorted_column,
        filters: this.report?.filters,
        currentWindow: { click: this.report.click_attribution_window, view: this.report.view_attribution_window },
        currency: this.lens?.currency,
        reportType: this.reportType
      }
    },
    reportRowCount () {
      return this.report?.selected_graph_rows[this.graphType]
    },
    totalAllowedRows () {
      return this.MAX_GRAPH_ROWS?.[this.graphType] ?? 8
    },
    isSelectedColsMaxed () {
      const currLength = this.selectedColumns[this.graphType].length
      return currLength >= this.graphInfo.data[this.graphType].total
    },
    // Pagination is updated before groups is prepared for render, hold load state during this step
    isPreparingGroups () {
      return this.getPagination.totalItems > 0 && this.tableResults?.ad_groups?.length === 0
    },
    noPaginationItems () {
      return this.getPagination.totalItems === 0
    },
    showGroupBreakdown () {
      return this.loading.groupBreakdown || this.groupBreakdownData !== null
    },
    showCreativeDetails () {
      return this.loading.adDetails || this.adDetailsData !== null
    },
    totalComparativeGroups () {
      return this.comparativeQuery?.length ?? 0
    },
    // Page meta
    reportPageTitle () {
      if (this.report?.name) return `${this.report.name} | Foreplay.co`
      return 'Foreplay.co | Lens'
    },
  },
  methods: {
    ...mapActions('LensModule', ['updateRowColor', 'updateColorFormat', 'updatePagination']),
    ...mapActions('MetricsModule', ['setMetricsPlatform', 'fetchPresets', 'createPreset', 'setPresetById', 'updateIsCustomPreset']),
    ...mapMutations('LensModule', ['SET_COLUMN_DRAWER_VIEW', 'SET_SHOW_REPORT_SHARE_MODAL', 'SET_SHOW_EDIT_TAG_MODAL']),

    // ============================================================================ \\
    // ============================= DATA METHODS ================================= \\
    // ============================================================================ \\

    async fetchLens () {
      const res = await LensAPI.Lens.get(this.$route.params.lensId)
      this.lens = res.data
    },
    async fetchAdGroups (query) {
      if (this.queryCancelToken) this.queryCancelToken.cancel()

      this.queryCancelToken = axios.CancelToken.source()

      this.loading.adGroups = true
      const report = this.report

      const page = query?.page

      const group = query?.group_by ?? this.report?.group_by
      const sort = query?.sort ?? this.report?.sorted_column
      const currentFilter = query?.filters ?? report.filters
      const currentPage = query?.group_by ? 1 : page?.currentPage ?? (this.getPagination?.currentPage || 1)
      const limit = page?.limit ?? (this.getPagination?.limit || 10)
      const currentWindow = query?.attribution_window ?? { click: this.report.click_attribution_window, view: this.report.view_attribution_window }
      const tableColumns = query?.tableColumns ?? this.report?.table_columns
      const customGroupQuery = this.comparativeQuery ? this.comparativeQuery.filter((group) => group.query.length > 0) : []

      const payload = {
        group,
        adAccountIds: this.selectedAdAccountIds,
        platform: this.getMetricsPlatform,
        filters: currentFilter,
        page: currentPage,
        limit,
        sort,
        currentWindow,
        selectedColumns: tableColumns,
        ...(this.reportType === 'comparative' && { comparativeQuery: customGroupQuery })
      }
      // Not copy/cloning because we do it before share creation, if you end up using this just be aware of that
      this.lastUsedPayload = payload
      try {
        const res = await LensAPI.AdGroups.getLensGroups(this.$route.params.lensId, payload, this.reportType, this.queryCancelToken.token)
        this.tableResults = res?.data ?? {}
        this.updatePagination({ ...this.getPagination, currentPage: currentPage, totalPages: res.pages, totalItems: res.total })
        if (res?.total === 0 || res.data.length === 0) {
          this.loading.adGroups = false
          return
        }

        // Prevents setting rows on pagination/query changes
        if (!this.hasMounted || !(query?.page || query?.limit) || query?.sort || query.group_by) {
          const maxRowsForGraph = this.MAX_GRAPH_ROWS[this.graphType]
          const rowCount = this.reportType === 'comparative' ? Math.min(res.data?.ad_groups?.length, maxRowsForGraph) : this.report?.selected_graph_rows[this.graphType]
          const adGroupRows = res.data?.ad_groups?.slice(0, rowCount) ?? []
          this.updateSelectedRows(adGroupRows)
          this.updateRowColor({ keys: adGroupRows, isAdding: true, graph: this.graphType, ignorePrevColors: true })
        }

        // Reset scroll position when data changes (not for pagination)
        this.$nextTick(() => {
          if (this.$refs.metricTable) {
            // Reset scroll position if this isn't a pagination change
            if (!(query?.page || query?.limit)) {
              this.$refs.metricTable.resetTableScroll()
            } else {
              // Just check the state for pagination changes
              this.$refs.metricTable.checkScrollState()
            }
          }
        })
        this.activeReqId = Date.now()
      } catch (e) {
        if (axios.isCancel(e)) {
          console.log('New query detected: Prev request canceled')
        } else {
          console.error(e)
          this.$showAlert({
            type: 'error',
            message: 'An unexpected error occurred'
          })
        }
      } finally {
        this.loading.adGroups = false
      }
    },

    // ============================================================================ \\
    // =========================== REPORT METHODS ================================= \\
    // ============================================================================ \\
    
    async fetchReport (id) {
      try {
        const { data: reportConfig } = await LensAPI.LensReport.getByID(id)
        if (_.isEmpty(reportConfig)) {
          this.reportNotFound = true
          return
        }
        reportConfig.table_config = this.normalizeTableConfig(reportConfig.table_config)
        this.reportConfig = reportConfig
        this.originalReportConfig = _.cloneDeep(reportConfig)
        this.initializeAdAccounts()
        this.initReportState(reportConfig)
      } catch (error) {
        console.error(error)
        this.$showAlert({ type: 'error', message: 'Failed to fetch report. See console for more details.' })
      }
    },
    initializeAdAccounts () {
      const connectedAdAccounts = this.lens.ad_accounts

      // If saved, initialize from the report config
      const storedAdAccountIds = this.reportConfig?.selected_ad_accounts
      if (storedAdAccountIds?.length) {
        // Check that none of the stored ad account ids were disconnected from the lens brand
        if (storedAdAccountIds.every(id => connectedAdAccounts.some(account => account.account_id === id))) {
          // To get the metrics platform, find the object that matches the first ad account id
          const firstAdAccount = connectedAdAccounts.find(account => account.account_id === storedAdAccountIds[0])
          this.setMetricsPlatform(reverseProviderKeyMap[firstAdAccount.provider])
          this.selectedAdAccountIds = storedAdAccountIds
          return
        }
      }

      // Otherwise, initialize using the platform on the first connected ad account
      const provider = connectedAdAccounts?.[0]?.provider
      const platformAdAccounts = connectedAdAccounts?.filter(account => account.provider === provider)
      if (platformAdAccounts?.length) {
        this.setMetricsPlatform(reverseProviderKeyMap[provider])
        const selectedAdAccountIds = platformAdAccounts.map(account => account.account_id)
        this.selectedAdAccountIds = selectedAdAccountIds
        // Update the report config with the new ad account ids
        this.reportConfig.selected_ad_accounts = selectedAdAccountIds
        this.originalReportConfig.selected_ad_accounts = selectedAdAccountIds
      } else {
        this.$showAlert({
          type: 'error',
          message: 'This lens brand does not have any ad accounts.'
        })
      }
    },
    normalizeTableConfig (tableConfig = {}) {
      return {
        ...tableConfig,
        colorFormat: tableConfig?.colorFormat || '',
        showStatus: tableConfig?.showStatus ?? false,
        showTags: tableConfig?.showTags ?? false,
        showCreationRange: tableConfig?.showCreationRange ?? false,
        showWeightedAvg: tableConfig?.showWeightedAvg ?? true,
        showSummation: tableConfig?.showSummation ?? true
      }
    },
    initReportState (config = null) {
      const reportConfig = config ?? this.reportConfig

      const isPlatformFirstInit = !this.reportPlatformInitialized[this.getMetricsPlatform]
      const reportData = getReportStateFromConfig(reportConfig)
      
      // Initialize type states (mounted only)
      if (!this.hasMounted) {
        this.reportType = reportData?.type
      }

      // Initialize preset (first platform init only)
      let presetId = null
      if (isPlatformFirstInit) {
        // Find the column hash for the preset associated with the report
        const presetColumnHash = this.getPresets.find(preset => preset.id === reportData.preset?.id)?.columns_hash
        // If the report column hash doesn't match the preset column hash, the preset is custom
        const isColHashEqual = presetColumnHash === reportData.columns_hash
        presetId = isColHashEqual ? reportData.preset?.id : null
        if (presetId) {
          this.updateIsCustomPreset(false)
          this.setPresetById({ id: presetId, shouldSelect: true })
        } else this.updateIsCustomPreset(true)
        this.wasCustom[this.getMetricsPlatform] = !isColHashEqual
      }

      // Initialize table config
      const dataTableConfig = reportData.table_config
      const tableConfig = {
        ...dataTableConfig,
        colorFormat: dataTableConfig?.colorFormat || '',
        showStatus: dataTableConfig?.showStatus || false,
        showTags: dataTableConfig?.showTags || false
      }
      this.updateColorFormat(tableConfig.colorFormat)

      // Initialize graph type
      const graphType = reportData?.graph_type ?? this.graphType
      this.graphType = graphType

      // Build the report object
      const report = {
        name: reportData?.name,
        description: reportData?.description,
        group_by: reportData?.group_by,
        filters: reportData?.filters,
        graph_type: graphType,
        click_attribution_window: reportData?.click_attribution_window,
        view_attribution_window: reportData?.view_attribution_window,
        selected_columns: reportData?.selected_columns,
        selected_graph_rows: reportData?.selected_graph_rows,
        table_columns: reportData?.table_columns,
        preset_id: presetId,
        custom_groups_filters: reportData?.custom_groups_filters,
        columns_hash: reportData?.columns_hash,
        sorted_column: reportData?.sorted_column || { by: 'spend', order: 'desc' },
        table_config: tableConfig
      }

      // Set the Data States
      this.report = report
      this.appliedFilters = parseFiltersFromQuery(reportData?.filters)
      this.selectedDateRange = parseDateRangeFromQuery(reportData?.filters)
      this.comparativeQuery = reportData?.custom_groups_filters || []
      // Initialize selected columns (rows are initialized based on query data)
      this.selectedColumns = { ...reportData?.selected_columns }

      if (isPlatformFirstInit) {
        // Mark the platform as initialized
        this.reportPlatformInitialized[this.getMetricsPlatform] = true
      }

      return report
    },
    setReportStateFieldValue (change) {
      const { type, value } = change

      const newConfig = applyChangeToReportConfig(this.reportConfig, { type, value })
      this.reportConfig = newConfig

      const report = getReportStateFromConfig(newConfig)
      this.report = report
      return report
    },
    handleReportChange (change) {
      const { type, value, preventFetch } = change

      this.setReportStateFieldValue(change)

      // If we are removing a column from that table that is selected for a graph, we need to filter the selected columns
      if (type === 'table_columns') {
        const filteredCols = { ...this.selectedColumns }
        Object.entries(this.selectedColumns).forEach(([graphType, columns]) => {
          filteredCols[graphType] = columns.filter(col => value.some(v => v.key === col))
        })
        this.selectedColumns = filteredCols
      }

      // Refetch the report data
      if (!preventFetch && !this.loading.cancel) {
        this.fetchAdGroups({
          [type]: value,
          ...(type === 'sorted_column' ? this.report.sorted_column : null)
        })
      }
    },
    // Updates that dont require a refetch
    handleUntrackedLocalReportChange (toUpdate) {
      const newConfig = mergeUpdateToReportConfig(this.reportConfig, toUpdate, this.getMetricsPlatform)
      this.reportConfig = newConfig
      this.originalReportConfig = mergeUpdateToReportConfig(this.originalReportConfig, toUpdate, this.getMetricsPlatform)
      this.report = getReportStateFromConfig(newConfig)
    },
    async handleSaveReport (toUpdate) {
      console.log('%c SAVING REPORT', 'color: orange; font-size: 18px')
      // Init async loading state
      this.editLoading = { type: null, value: true }
      try {
        const reportId = this.report?.id || this.$route.params?.reportId
        await LensAPI.LensReport.update(reportId, toUpdate)

        // Show success message & wait before dismiss & refetch
        this.editLoading = { type: 'success', value: false }
        setTimeout(async () => {
          this.editLoading = { type: null, value: false }
        }, 1500)

        this.wasCustom[this.getMetricsPlatform] = this.getIsCustomPreset
        this.originalReportConfig = _.cloneDeep(this.reportConfig)
      } catch (e) {
        this.$showAlert({ type: 'error', message: e.message })
        this.editLoading = { type: null, value: false }
      }
    },
    async handleReportCancel (shouldFetch = false) {
      this.loading.cancel = true
      const originalConfig = _.cloneDeep(this.originalReportConfig)
      this.reportConfig = originalConfig
      const originalReport = this.initReportState(originalConfig)

      // Reset selected ad account ids
      const originalAdAccountIds = originalConfig?.selected_ad_accounts
      if (originalAdAccountIds?.length) {
        // Find the object that matches the first ad account id and get its platform
        const firstAdAccount = this.lens.ad_accounts.find(account => account.account_id === originalAdAccountIds[0])
        if (firstAdAccount) {
          this.setMetricsPlatform(reverseProviderKeyMap[firstAdAccount.provider])
          this.selectedAdAccountIds = originalAdAccountIds
        }
      }

      // Reset preset (if applicable)
      const presetId = originalReport.preset_id
      if (presetId) this.setPresetById({ id: presetId, shouldSelect: true })
      this.updateIsCustomPreset(this.wasCustom[this.getMetricsPlatform] ?? true)

      // Refetch ad groups (if applicable)
      if (shouldFetch) await this.fetchAdGroups()

      // Reset rows to first X (rowCount) rows
      const rowCount = originalReport?.selected_graph_rows[this.graphType]
      this.selectedRows = this.tableResults.ad_groups.slice(0, rowCount)
      this.updateRowColor({ keys: this.selectedRows, isAdding: true, graph: this.graphType, ignorePrevColors: true })
      this.loading.cancel = false
    },
    
    // ============================================================================ \\
    // ========================= FILTER/PRESET METHODS ============================ \\
    // ============================================================================ \\

    handleAdAccountSelectionChange (selection) {
      const { accountIds, platform } = selection
      this.setMetricsPlatform(platform)
      this.selectedAdAccountIds = accountIds

      const newConfig = { ...this.reportConfig, selected_ad_accounts: accountIds }
      this.reportConfig = newConfig
      this.initReportState(newConfig)
      this.fetchAdGroups()
    },
    handleFiltersChange (filters, dateRange) {
      if (!this.hasMounted) return
      const filterQuery = createFilterQuery(filters, dateRange)
      this.setReportStateFieldValue({ type: 'filters', value: filterQuery })
      if (!this.loading.cancel) this.fetchAdGroups({ filters: filterQuery })
    },
    async handleNewPreset (preset) {
      try {
        const newPreset = { ...preset }
        await this.createPreset(newPreset)
      } catch (e) {
        this.$showAlert({ type: 'error', message: e.message })
      }
    },

    // ============================================================================ \\
    // ============================= TABLE METHODS ================================ \\
    // ============================================================================ \\

    updateTableResults (update) {
      const { index, value, key, findKey } = update || {}
      if (!findKey) {
        this.$set(this.tableResults?.ad_groups?.[index], key, value)
      } else {
        const indexToUpdate = this.tableResults?.ad_groups?.findIndex(ad => ad.group_by === findKey)
        this.$set(this.tableResults?.ad_groups?.[indexToUpdate], key, value)
      }
    },
    updateSelectedRows (rows) {
      this.selectedRows = rows

      const rowLength = rows.length
      // Consistent row selection until better ux is determined
      const updatedReportGraphRows = { ...this.report.selected_graph_rows, [this.graphType]: rowLength }
      this.setReportStateFieldValue({ type: 'selected_graph_rows', value: updatedReportGraphRows })
      this.updateRowColor({ keys: rows, isAdding: true, graph: this.graphType })
    },
    updateSelectedColumns (columns, graphTypeOverride = null) {
      // We need to check if we're adding new columns to the report
      const graphType = graphTypeOverride ?? this.graphType

      const existingTableColumns = new Set(this.report?.table_columns?.map(col => col.key) || [])
      const newTableColumns = [...columns].filter(column => !existingTableColumns.has(column))
      if (newTableColumns.length) {
        // If there are new columns, we need to update the table_columns state to include them
        const change = {
          type: 'table_columns',
          value: [
            ...this.report?.table_columns || [],
            ...newTableColumns.map(col => ({ key: col, is_pinned: false }))
          ],
          preventFetch: false
        }
        this.handleReportChange(change)
        this.updateIsCustomPreset(true) // Flag the preset as custom
      }

      // Update selected columns (prevent foreplay metrics from being selected for line graph)
      const newColumns = graphType === 'line'
        ? [...columns.filter(col => this.getMetricLookup?.[col.key]?.parent_group !== 'foreplay_metrics')]
        : [...columns]
      this.selectedColumns[graphType] = newColumns
      const updatedReportSelectedColumns = { ...this.report.selected_columns, [graphType]: newColumns }
      this.setReportStateFieldValue({ type: 'selected_columns', value: updatedReportSelectedColumns })
    },
    handleSearchChange (query) {
      this.searchQuery = query
    },
    handlePaginationChange (change) {
      const { type, value, preventFetch } = change

      // If no type specified, spread into object parents
      const newPagination = type ? {
        ...this.getPagination,
        [type]: value
      } : {
        ...this.getPagination,
        ...value
      }
      
      // Persist limit to localStorage when it changes
      if (type === 'limit' || (value && value.limit !== undefined)) {
        const limitValue = type === 'limit' ? value : value.limit
        this.storeLimit(limitValue)
      }
      
      this.updatePagination(newPagination)
      if (!preventFetch) {
        this.fetchAdGroups({ page: value?.currentPage, limit: value?.limit })
      }
    },

    // ============================================================================ \\
    // ============================ DRAWER METHODS ================================ \\
    // ============================================================================ \\

    async handleShowDetailsDrawer (row, additionalColumns = []) {
      if (additionalColumns.length) {
        // Additional columns are only populated when refetching from the insights drawer after a preset change
        this.loading.adDetailsMetrics = true
      } else {
        this.loading.adDetails = true
        this.adDetailsRow = row
      }
      const payload = this.createTopPayload(row?.group_by || this.report?.group_by, additionalColumns, true)
      try {
        // const payload = this.reportType === 'comparative' ? this.createComparativePayload(groupKey) : this.createTopPayload(groupKey)
        const firstAd = row.ads[0]
        const { groupBy, ...query } = payload
        const idPayload = {
          lensId: this.$route.params.lensId,
          creativeId: firstAd.creative_id,
          creativeHash: firstAd?.creative_hash,
          adId: firstAd.ad_id
        }
        const [adDetails, creativeTags] = await Promise.all([
          LensAPI.AdGroups.getLensCreativeGroup(idPayload, query),
          LensAPI.Tags.getTagsByCreativeHash(this.$route.params?.lensId, firstAd?.creative_hash)
        ])

        this.adDetailsData = {
          ...firstAd,
          ...adDetails.data,
          aiTransaction: adDetails.data.ai_transaction,
          creativeTags: creativeTags?.data,
          // For share purposes
          detailsQuery: {
            groupBy: this.report?.group_by,
            query,
            date: this.selectedDateRange
          },
          isCreativeGroup: true
        }
      } catch (e) {
        if (axios.isCancel(e)) {
          console.log('New query detected: Prev request canceled')
        } else {
          console.error(e)
          this.$showAlert({
            type: 'error',
            message: 'An unexpected error occurred'
          })
        }
      } finally {
        this.loading.adDetails = false
        this.loading.adDetailsMetrics = false
      }
    },
    handleShowGroupBreakdown (row) {
      this.setComparativeBreakdown(row.group_by)
    },
    async setComparativeBreakdown (groupKey) {
      this.loading.groupBreakdown = true
      try {
        const payload = this.reportType === 'comparative' ? this.createComparativePayload(groupKey) : this.createTopPayload(groupKey)
        const res = await LensAPI.AdGroups.getLensGroupAds(this.lens.id, payload, this.reportType)
        const group = res.data.ad_groups[0]
        this.groupBreakdownData = {
          ...group,
          previous_period: group.previous_period || {},
          summary_stats: res.data.summary_stats || {}
        }
      } catch (e) {
        if (axios.isCancel(e)) {
          console.log('New query detected: Prev request canceled')
        } else {
          console.error(e)
          this.$showAlert({
            type: 'error',
            message: 'An unexpected error occurred'
          })
        }
      } finally {
        this.loading.groupBreakdown = false
      }
    },
    handleCloseBreakdown () {
      LensAPI.AdGroups.abortBreakdownRequest()
      this.groupBreakdownData = null
    },
    createTopPayload (groupKey, additionalColumns = [], isCreativeInsightsRequest = false) {
      const groupFilter = [{ key: 'group_by', value: groupKey, operation: 'equals' }]
      const queryFilter = isCreativeInsightsRequest ? [...this.report.filters] : [...this.report.filters, groupFilter]

      // In case we want to fetch additional columns that are not currently used in the table
      const selectedColumns = [...new Set([
        ...additionalColumns.map(key => ({ key, is_pinned: false })),
        ...this.report.table_columns,
        ...(this.lens?.goal_metric ? [{ key: this.lens.goal_metric, is_pinned: false }] : []) // Make sure goal metric is always included
      ])]

      // Do not include foreplay metrics in creative insights requests (we will always include them in a separate card)
      const payloadSelectedColumns = isCreativeInsightsRequest
        ? selectedColumns.filter(col => this.getMetricLookup?.[col.key]?.parent_group !== 'foreplay_metrics')
        : selectedColumns

      const payload = {
        adAccountIds: this.selectedAdAccountIds,
        platform: this.getMetricsPlatform,
        groupBy: this.report.group_by,
        currentWindow: {
          click: this.report.click_attribution_window,
          view: this.report.view_attribution_window
        },
        filters: queryFilter,
        selectedColumns: payloadSelectedColumns
      }
      return payload
    },
    createComparativePayload (condId) {
      const conditionData = this.comparativeQuery.filter((group) => group.group_by === condId)
      const groupFilter = [{ key: 'group_by', value: condId, operation: 'equals' }]
      const queryFilter = [...this.report.filters, groupFilter]
      const payload = {
        adAccountIds: this.selectedAdAccountIds,
        platform: this.getMetricsPlatform,
        groupBy: this.report.group_by,
        currentWindow: {
          click: this.report.click_attribution_window,
          view: this.report.view_attribution_window
        },
        comparativeQuery: conditionData,
        filters: queryFilter,
        selectedColumns: this.report.table_columns
      }
      return payload
    },

    // ============================================================================ \\
    // ================================ UI METHODS ================================ \\
    // ============================================================================ \\

    closeColumnDrawer () {
      this.SET_COLUMN_DRAWER_VIEW(false)
    },
    handleScrollStateChange (scrollState) {
      this.tableScrollState = scrollState
    },
    handleTableScroll (direction) {
      if (!this.$refs.metricTable) return
      if (direction === 'left') {
        this.$refs.metricTable.scrollLeft()
      } else if (direction === 'right') {
        this.$refs.metricTable.scrollRight()
      }
    },
    updateEditLoad (type, value) {
      this.editLoading = {
        type,
        value
      }
    },
    applyToolbarStickyStyles () {
      const filterBar = this.$refs.filterBar
      if (!filterBar) return

      if (this.stickToViewport) {
        filterBar.style.position = 'sticky'
        filterBar.style.top = '16px'
        filterBar.style.zIndex = '40000'
      } else {
        // Unlock the toolbar so it scrolls away with the content
        filterBar.style.position = 'static'
        filterBar.style.top = ''
        filterBar.style.zIndex = ''
        // Reset any scroll-driven styling so it doesn't linger while unlocked
        filterBar.classList.remove('toolbar-container')
        filterBar.style.marginLeft = ''
        filterBar.style.marginRight = ''
        filterBar.style.marginTop = ''
        filterBar.style.removeProperty('border-radius')
      }
      this.measureFilterBarHeight()
    },
    measureFilterBarHeight() {
      this.$nextTick(() => {
        const filterBar = this.$refs.filterBar
        if (filterBar) {
          this.filterBarHeight = filterBar.getBoundingClientRect().height
        }
      })
    },
    onScroll (e, position) {
      // When the toolbar is unlocked we leave it in normal flow and skip the
      // scroll-driven sticky animation entirely.
      if (!this.stickToViewport) return

      const marginValue =
        position.scrollTop / 6 < 16 ? position.scrollTop / 6 : 16
      const rounding =
        position.scrollTop / 10 < 5 ? position.scrollTop / 10 : 5
      if (this.$refs.filterBar) {
        this.$refs.filterBar.style.marginLeft = `${marginValue}px`
        this.$refs.filterBar.style.marginRight = `${marginValue}px`
        this.$refs.filterBar.style.marginTop = `${marginValue}px`
        this.$refs.filterBar.style.borderRadius = `${rounding}px`
      }
      const shadowSection = (maxValue) => {
        return position.scrollTop / 15 < maxValue
          ? position.scrollTop / 15
          : maxValue
      }

      if (position.scrollTop && this.$refs.filterBar) {
        if (position.scrollTop > 0) this.$refs.filterBar.classList.add('toolbar-container')
        this.$refs.filterBar.style.setProperty('border-radius', `${shadowSection(14)}px`, 'important')
      } else {
        if (this.$refs.filterBar) {
          this.$refs.filterBar.classList.remove('toolbar-container')
        }
      }
    },
    
    // ============================================================================ \\
    // =============================== MISC METHODS =============================== \\
    // ============================================================================ \\

    getStoredLimit () {
      const storedLimit = localStorage.getItem('lens_report_pagination_limit')
      return storedLimit ? parseInt(storedLimit, 10) : 10 // Default to 10
    },
    storeLimit (limit) {
      localStorage.setItem('lens_report_pagination_limit', limit.toString())
    },

    handleCSVExport () {
      const csvHeaders = this.report?.table_columns
      const csvData = this.tableResults?.ad_groups
      const groupBy = this.report?.group_by
      const csvContent = formatGroupsAsCSV(csvHeaders, csvData, groupBy)

      if (!csvContent) {
        this.$showAlert({ type: 'error', message: 'Unable to generate a csv from this data' })
        return
      }

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8,' })
      const objUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', objUrl)
      link.setAttribute('download', `${this.report?.name || 'Report Export'}.csv`)

      document.body.appendChild(link)
      link.click()
      this.$showAlert({ type: 'success', message: 'CSV exported successfully' })
      link.remove()
    },
    openIntercom () {
      window.Intercom('show')
    }
  }
}
</script>

<style scoped>
  .dropdown-scrollable {
    transition: colors 250ms ease-in-out;
    top: 100%;
    z-index: 999;
  }
  .dropdown-scrollable::-webkit-scrollbar {
    width: 3px;
  }
  .dropdown-scrollable::-webkit-scrollbar-thumb {
    background-color: #DFE1E7;
    border-radius: 18px;
  }

  .grid-background{
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  background-image: linear-gradient(to right, #ECEFF3 1px, transparent 1px),
    linear-gradient(to bottom, #ECEFF3 1px, transparent 1px);
  background-size: 124px 82px;
  }

  .tableFade-enter-active,
  .tableFade-leave-active {
    transition: all 0.25s ease-out;
  }

  .tableFade-enter-from {
    opacity: 0;
    transform: translateY(1rem);
  }

  .tableFade-leave-to {
    opacity: 0;
    transform: translateY(-1rem);
  }
  .base-btn {
    box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13),
      0px 0px 0px 1px rgba(0, 56, 108, 0.08);
    transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
    border-radius: 6px;
    padding: 6px 8px 6px 8px;
  }

  .base-btn:hover {
    box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17),
      0px 0px 0px 1px rgba(0, 56, 108, 0.12);
  }

  .toolbar-container{
    border: none !important;
    box-shadow:
      0px 28px 28px -14px rgba(9, 25, 72, 0.06),
      0px 12px 12px -6px rgba(9, 25, 72, 0.06),
      0px 3px 3px -1.5px rgba(9, 25, 72, 0.06),
      0px 1px 1px -0.5px rgba(9, 25, 72, 0.06),
      0px 0px 0.5px 0.75px rgba(9, 25, 72, 0.06) !important
  }

  /* Section transitions */
  .expand-enter-active,
  .expand-leave-active {
    transition-property: opacity, height;
    transition-duration: 250ms;
  }

  .expand-enter,
  .expand-leave-to {
    opacity: 0;
  }
</style>
